import { createContext, useCallback, useContext, useState, type ReactNode } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { CheckCircle2, Info, TriangleAlert, X, XCircle } from 'lucide-react'
import { cn } from '../lib/cn'
import { cheer, DID } from '../lib/hapiMood'

type ToastVariant = 'success' | 'info' | 'warning' | 'error'

/**
 * What else a toast can carry.
 *
 * `undo` is the important one. Marking something done is one tap away from
 * marking the wrong thing done, and hunting back through a board to put it
 * right is a minute of somebody's day; a button that sits there for a few
 * seconds costs nothing and saves all of it. A toast with one waits longer
 * than a toast that is only telling you something — six seconds rather than
 * three — because it is asking a question, not making a remark.
 */
export interface ToastExtras {
  undo?: () => void | Promise<void>
  /** What the button says, when "Undo" is not the word. */
  undoLabel?: string
}

interface Toast {
  id: number
  message: string
  variant: ToastVariant
  undo?: (() => void | Promise<void>) | undefined
  undoLabel?: string | undefined
}

interface ToastContextValue {
  showToast: (message: string, variant?: ToastVariant, extras?: ToastExtras) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

const variantConfig: Record<ToastVariant, { icon: typeof CheckCircle2; className: string }> = {
  success: { icon: CheckCircle2, className: 'text-success-500' },
  info: { icon: Info, className: 'text-info-500' },
  warning: { icon: TriangleAlert, className: 'text-warning-500' },
  error: { icon: XCircle, className: 'text-danger-500' },
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])

  const showToast = useCallback((message: string, variant: ToastVariant = 'success', extras: ToastExtras = {}) => {
    // Hapi hears every toast, which is the cheapest honest way to make him
    // react to the whole app rather than to the six places somebody remembered
    // to tell him. A call site that wants a particular pose says so itself,
    // just before or after; the last word wins and that is the specific one.
    //
    // Nothing is sent for an undoable toast. "Marked done — undo?" is a
    // question, and a mascot celebrating while the answer is still open is a
    // mascot celebrating the wrong thing a fair fraction of the time.
    if (extras.undo === undefined) {
      cheer(variant === 'error' ? DID['wentWrong']! : { pose: 'happy' })
    }

    const id = Date.now() + Math.random()
    setToasts((prev) => [
      ...prev,
      {
        id,
        message,
        variant,
        ...(extras.undo === undefined ? {} : { undo: extras.undo }),
        ...(extras.undoLabel === undefined ? {} : { undoLabel: extras.undoLabel })
      }
    ])
    setTimeout(
      () => {
        setToasts((prev) => prev.filter((t) => t.id !== id))
      },
      extras.undo === undefined ? 3200 : 6000
    )
  }, [])

  const dismiss = (id: number) => setToasts((prev) => prev.filter((t) => t.id !== id))

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      {/*
        * Lifted above Hapi from `lg` up, which is exactly where he appears.
        * He stands in this corner and the toasts come up through it, and the
        * stack is the thing that moves because it is the thing that is only
        * there for three seconds.
        *
        * Below `lg` the bottom bar takes the last 60-odd pixels of the
        * screen, so the stack sits above it rather than over it — an Undo
        * behind the bar is an Undo nobody sees.
        */}
      <div className="fixed inset-x-4 bottom-[calc(env(safe-area-inset-bottom)+5rem)] z-[9998] flex flex-col items-end gap-2 sm:left-auto sm:right-6 lg:bottom-32">
        <AnimatePresence>
          {toasts.map((toast) => {
            const config = variantConfig[toast.variant]
            const Icon = config.icon
            return (
              <motion.div
                key={toast.id}
                initial={{ opacity: 0, y: 12, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, scale: 0.96, transition: { duration: 0.15 } }}
                transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                className="flex items-center gap-2.5 rounded-2xl border border-shell-border bg-shell/95 px-4 py-3 shadow-pop backdrop-blur-sm min-w-[260px] max-w-sm"
              >
                <Icon className={cn('h-4.5 w-4.5 shrink-0', config.className)} size={18} />
                <p className="text-sm text-shell-text-active flex-1">{toast.message}</p>
                {toast.undo !== undefined && (
                  <button
                    onClick={() => {
                      dismiss(toast.id)
                      void toast.undo?.()
                    }}
                    className="shrink-0 rounded-lg px-2 py-1 text-sm font-semibold text-brand-500 transition-colors hover:bg-brand-500/10"
                  >
                    {toast.undoLabel ?? 'Undo'}
                  </button>
                )}
                <button
                  aria-label="Dismiss notification"
                  onClick={() => dismiss(toast.id)}
                  className="text-shell-text hover:text-shell-text-active transition-colors"
                >
                  <X size={14} />
                </button>
              </motion.div>
            )
          })}
        </AnimatePresence>
      </div>
    </ToastContext.Provider>
  )
}

export function useToast() {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast must be used within ToastProvider')
  return ctx
}
